import { z } from 'zod';

import { requiredFieldMessage } from '@/shared/lib/i18n';
import type { MasterRow } from '../master-table/with-deletable-flag';
import { computeVisibleLevels } from './build-master-autocomplete-options';

/**
 * マスタ新規追加フォームの基本スキーマ
 *
 * level1 は必須。level2〜4 はオプション。
 */
const masterCreateBaseSchema = z.object({
  level1_code: z.string().min(1),
  level1_name: z.string().min(1),
  level2_code: z.string().optional(),
  level2_name: z.string().optional(),
  level3_code: z.string().optional(),
  level3_name: z.string().optional(),
  level4_code: z.string().optional(),
  level4_name: z.string().optional(),
});

export type MasterCreateFormValues = z.infer<typeof masterCreateBaseSchema>;

/**
 * マスタ編集フォームのバリデーションスキーマ
 *
 * 末端名称（leaf_name）のみ編集可能なので、全フィールドの形式は維持しつつ
 * 末端名称が空でないことだけをチェックする。
 */
export const createMasterEditSchema = (leafLevel: 1 | 2 | 3 | 4) => {
  return masterCreateBaseSchema.refine(
    (data) => {
      const leafNameField = `level${leafLevel}_name` as keyof typeof data;
      return !!data[leafNameField];
    },
    {
      path: [`level${leafLevel}_name`],
      message: requiredFieldMessage(),
    },
  );
};

/**
 * 既存マスタデータを考慮した動的バリデーションスキーマを生成する
 *
 * 既存コードが入力された場合、次のレベルの入力を必須にする。
 * 未登録コードの場合はそのレベルのみで登録可能。
 */
export const createMasterCreateSchema = (existingRows: MasterRow[]) => {
  return masterCreateBaseSchema.superRefine((data, ctx) => {
    const { showLevel2, showLevel3, showLevel4 } = computeVisibleLevels({
      existingRows,
      formData: data,
    });

    // 表示されるレベルは必須（既存コードの場合、次レベルで差分化が必要）
    if (showLevel2) {
      if (!data.level2_code) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['level2_code'],
          message: requiredFieldMessage(),
        });
      }
      if (!data.level2_name) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['level2_name'],
          message: requiredFieldMessage(),
        });
      }
    }

    if (showLevel3) {
      if (!data.level3_code) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['level3_code'],
          message: requiredFieldMessage(),
        });
      }
      if (!data.level3_name) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['level3_name'],
          message: requiredFieldMessage(),
        });
      }
    }

    if (showLevel4) {
      if (!data.level4_code) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['level4_code'],
          message: requiredFieldMessage(),
        });
      }
      if (!data.level4_name) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['level4_name'],
          message: requiredFieldMessage(),
        });
      }
    }
  });
};
